import { useRef, useState, memo, useMemo } from 'react'
import { PhotoIcon } from '@heroicons/react/24/outline'
import { trackGalleryImageClick, trackGalleryLoadMore } from '../utils/analytics'
import ImageWithFallback from './ImageWithFallback'

function EntityGallery({ images = [], entityId, onImageClick }) {
  const [showAll, setShowAll] = useState(false)
  
  // Early return for empty images - must come before hooks to prevent errors
  if (!images || images.length === 0) {
    return null
  }
  
  // Show first 9 images, or all if showAll is true
  // Memoize to avoid recalculating on every render
  const displayImages = useMemo(() => 
    showAll ? images : images.slice(0, 9),
    [images, showAll]
  )
  const hasMore = images.length > 9
  
  const toggleShowAll = () => setShowAll(prev => !prev)

  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
        <PhotoIcon className="w-5 h-5 mr-2 text-primary" />
        Community Photos ({images.length})
      </h3>
      <div className="grid grid-cols-3 gap-2">
        {displayImages.map((image) => (
          <div key={`${image.id}-${image.isPrimary}`} className={`aspect-square rounded-lg overflow-hidden bg-gray-200 relative ${image.isPrimary ? 'opacity-100' : 'opacity-95'}`}>
              <div className="absolute inset-0 animate-pulse bg-gray-300" />
              {image.isPrimary && (
                <div key={`star-${image.id}`} className="absolute top-1 right-1 z-10 bg-yellow-400 text-white rounded-full p-1 shadow-lg">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
              )}
              <ImageWithFallback
                key={`img-${image.id}-${image.isPrimary}`}
                src={image.imageUrl}
                alt="Community photo"
                className="relative z-10 w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity hover:scale-105 transform transition-transform duration-200"
                onClick={() => {
                  const imageIndex = displayImages.indexOf(image)
                  trackGalleryImageClick(entityId, image.id, image.isPrimary, imageIndex)
                  if (onImageClick) {
                    onImageClick(image, imageIndex)
                  } else {
                    // Fallback to opening in new tab if no handler provided
                    const url = image.imageUrl?.startsWith('http') ? image.imageUrl : `http://localhost:8081${image.imageUrl}`
                    window.open(url, '_blank')
                  }
                }}
                showIconFallback={false}
              />
            </div>
          ))}
      </div>

      {/* Load More Button */}
      {!showAll && hasMore && (
        <button
          onClick={() => {
            trackGalleryLoadMore(entityId, images.length, 9)
            toggleShowAll()
          }}
          className="w-full py-2 px-4 mt-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors text-sm"
        >
          Load More ({images.length - 9} more images)
        </button>
      )}
    </div>
  )
}

// Memoize component - only re-render if images array length or entityId changes
export default memo(EntityGallery, (prevProps, nextProps) => {
  const prevLength = prevProps.images?.length || 0
  const nextLength = nextProps.images?.length || 0
  return prevLength === nextLength &&
         prevProps.entityId === nextProps.entityId
})
